import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import AlertOverlay from "../../../components/Alert";
import { useNavigate } from "react-router-dom";
import { useWarehouseUserClientReportDaily } from "../../../apis/warehouseUserClientReportDaily/WarehouseUserClientReportDailyProvider";
import { getUiInterface, getPageName, getWordConnector, getErrorMessage, getSuccessMessage } from "../../../locales/translationUtils";
import { WarehouseUserClientReportDailyVM } from "../../../services/warehouseUserClientReportDailyService";
import { FaChevronDown } from "react-icons/fa";
import NavBar, { NavBarItem } from '../../../components/NavBar';
import { useGeneric } from '../../../apis/generic/GenericProvider';
import { useUser } from "../../../apis/user/UserProvider";
import User from "../../../types/user";
import { FILTRO_TODOS_OS_USUARIOS } from "../../../services/userService";

const WarehouseUserClientReportDailyCreateView: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { postWarehouseUserClientReportDaily } = useWarehouseUserClientReportDaily();
    const navigate = useNavigate();
    const { distinctOptions, fetchDistinctOptions } = useGeneric();
    const { fetchUsuarios } = useUser();
    const [usuarios, setUsuarios] = useState<User[]>([]);

    const [warehouseUserClientReportDaily, setWarehouseUserClientReportDaily] = useState<WarehouseUserClientReportDailyVM>({
        version: 0,
        clientId: "",
        wrId: "",
        userId: ""
    });

    useEffect(() => {
        fetchDistinctOptions('wareHouse');
        fetchDistinctOptions('client');
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const usuariosCarregados = await fetchUsuarios(FILTRO_TODOS_OS_USUARIOS);
            setUsuarios(usuariosCarregados);
        };

        fetchData();
    }, []);

    const navItems: NavBarItem[] = [
        { label: t('warehouseUserClientReportDaily.labels.home'), route: "/", iconName: "house-door" },
        { label: t('warehouseUserClientReportDaily.labels.list'), route: "/WarehouseUserClientReportDaily", iconName: "table" },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setWarehouseUserClientReportDaily((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nullRequiredFields: string[] = [];
        if (!warehouseUserClientReportDaily.clientId) nullRequiredFields.push(t('warehouseUserClientReportDaily.fields.client'));
        if (!warehouseUserClientReportDaily.wrId) nullRequiredFields.push(t('warehouseUserClientReportDaily.fields.from'));
        if (!warehouseUserClientReportDaily.userId) nullRequiredFields.push(t('warehouseUserClientReportDaily.fields.user'));

        if (nullRequiredFields.length > 0) {
            const warnMessage: string =
                nullRequiredFields.length === 1
                    ? getUiInterface("fieldRequiredSingle", {
                        field: nullRequiredFields[0],
                    })
                    : getUiInterface("fieldRequiredMultiple", {
                        fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                            ? ` ${getWordConnector("and")} `
                            : ""
                            }${nullRequiredFields.slice(-1)}`,
                    });

            setAlert({ message: warnMessage, variant: "danger" });
            return;
        }

        try {
            const response = await postWarehouseUserClientReportDaily(warehouseUserClientReportDaily);

            if (response?.messages == null && response?.data?.id) {
                const successMessage = getSuccessMessage("createWarehouseUserClientReportDaily", { id: response.data.id });
                navigate(`/WarehouseUserClientReportDaily`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                setAlert({
                    message: getErrorMessage("genericError", { error: response?.messages }),
                    variant: "danger",
                });
            }
        } catch (error) {
            setAlert({
                message: getErrorMessage("genericError", {}),
                variant: "danger",
            });
        }
    };

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-3"></div>
            <h2>{getPageName("createWarehouseUserClientReportDaily")}</h2>
            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={12} className="mb-3">
                        <Form.Group controlId="client">
                            <Form.Label>{t('warehouseUserClientReportDaily.fields.client')}</Form.Label>
                            <div style={{ position: "relative", width: "20%" }}>
                                <Form.Control
                                    as="select"
                                    name="clientId"
                                    value={warehouseUserClientReportDaily.clientId}
                                    onChange={handleChange}
                                >
                                    <option value="">{t('warehouseUserClientReportDaily.labels.selectOption')}</option>
                                    {distinctOptions['client']?.map(
                                        (option: { id: number; description: string }) => (
                                            <option key={option.id} value={option.id.toString()}>
                                                {option.description}
                                            </option>
                                        )
                                    )}
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                    }}
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col md={12} className="mb-3">
                        <Form.Group controlId="wr">
                            <Form.Label>{t('warehouseUserClientReportDaily.fields.from')}</Form.Label>
                            <div style={{ position: "relative", width: "20%" }}>
                                <Form.Control
                                    as="select"
                                    name="wrId"
                                    value={warehouseUserClientReportDaily.wrId}
                                    onChange={handleChange}
                                >
                                    <option value="">{t('warehouseUserClientReportDaily.labels.selectOption')}</option>
                                    {distinctOptions['wareHouse']?.map(
                                        (option: { id: number; description: string }) => (
                                            <option key={option.id} value={option.id.toString()}>
                                                {option.description}
                                            </option>
                                        )
                                    )}
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                    }}
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col md={12} className="mb-3">
                        <Form.Group controlId="wr">
                            <Form.Label>{t('warehouseUserClientReportDaily.fields.user')}</Form.Label>
                            <div style={{ position: "relative", width: "20%" }}>
                            <Form.Control
                                    as="select"
                                    name="userId"
                                    value={warehouseUserClientReportDaily.userId}
                                    onChange={handleChange}
                                >
                                    <option value="">{t('warehouseUserClientReportDaily.labels.selectOption')}</option>
                                    {usuarios?.map((user) => (
                                        <option key={user.id} value={String(user.id)}>
                                            {user.fullName}
                                        </option>

                                    ))}
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                    }}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <Button variant="primary" type="submit">
                    {getUiInterface("create")}
                </Button>
            </Form>
        </Container>
    );
};

export default WarehouseUserClientReportDailyCreateView;
